import { useCallback, useMemo, useState, ReactNode, ReactElement } from "react";
import { MelissaAddressFieldConfig } from "@evergiving/common/dist/es2015/form";
import {
  PostalAddressKind,
  MelissaResponseAddress,
} from "@evergiving/common/dist/es2015/pledge";
import { useDispatch, useSelector } from "react-redux";
import { notNil } from "@evergiving/common/dist/es2015/typescriptUtils";

import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { AddressPill } from "@/lib/components/AddressPill";
import { Text } from "@/lib/components/Text";
import { Spinner } from "@/lib/components/Spinner";
import { setPostalAddress } from "@/lib/redux/actions";
import { MelissaResponse } from "@/lib/misc/MelissaResponse";
import { FormattedNumber } from "@/lib/components/FormattedNumber";
import { RootState } from "@/lib/redux/types";

export const SearchAddressField = ({
  locale,
  melissaConfig,
  results,
  onChange,
  onSearch,
  onClear,
  loading,
  value,
  defaultCountryCode,
}: {
  locale: string;
  melissaConfig: MelissaAddressFieldConfig;
  results: MelissaResponse | null;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  onClear: () => void;
  loading: boolean;
  value: string;
  defaultCountryCode: string;
}): ReactElement => {
  const maxNumShown = 5;

  const pills: ReactNode[] = [];

  const addresses: MelissaResponseAddress[] | null = results?.Results
    ? results.Results.map((result) => result.Address).filter(notNil)
    : null;

  const numResults = addresses ? addresses.length : null;
  const numShown = Math.min(numResults || 0, maxNumShown);
  const shownAddresses: MelissaResponseAddress[] = useMemo(
    () => (addresses ? addresses.slice(0, numShown) : []),
    [addresses, numShown]
  );

  const [selectedAddress, setSelectedAddress] = useState<string>("");

  const selectedFullAddress:
    | MelissaResponseAddress
    | undefined = shownAddresses.find(
    (address) => address.Address === selectedAddress
  );
  shownAddresses.forEach((address, index) => {
    pills.push(
      <AddressPill
        className="addressMatch"
        key={address.Address}
        address={{ kind: PostalAddressKind.Melissa, ...address }}
        selected={selectedFullAddress === address}
        scrollIntoView={
          selectedFullAddress
            ? selectedFullAddress === address
            : index === shownAddresses.length - 1
        }
      >
        <div className="myAddressIs">
          <Text
            value={melissaConfig.addressSuggestionTemplateText}
            args={{ suggestion: <span>{address.Address}</span> }}
          />
        </div>
      </AddressPill>
    );
  });
  const dispatch = useDispatch();

  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const handleShowFreeFormAddress = useCallback(() => {
    dispatch(
      setPostalAddress({
        kind: PostalAddressKind.FreeForm,
        element2: currentCountryCode ?? defaultCountryCode,
        element3: undefined,
        element4: undefined,
        element5: undefined,
        element6: undefined,
        element7: undefined,
        element8: undefined,
        element9: undefined,
        element10: undefined,
        element11: undefined,
        element12: undefined,
        element13: undefined,
        element14: undefined,
        element15: undefined,
      })
    );
  }, [dispatch, currentCountryCode]);

  const moveSelection = useCallback(
    (delta: number) => {
      if (shownAddresses) {
        const selectedIndex = shownAddresses.findIndex(
          (address) => address.Address === selectedAddress
        );
        const newSelectedIndex =
          (selectedIndex + shownAddresses.length + delta) %
          shownAddresses.length;
        setSelectedAddress(
          addresses ? addresses[newSelectedIndex].Address : ""
        );
      }
    },
    [selectedAddress, shownAddresses, addresses]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        moveSelection(1);
      } else if (event.key === "ArrowUp") {
        moveSelection(-1);
      } else if (event.key === "Escape") {
        onClear();
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (selectedFullAddress) {
          dispatch(
            setPostalAddress({
              kind: PostalAddressKind.Melissa,
              ...selectedFullAddress,
            })
          );
        } else {
          onSearch();
        }
      }
    },
    [moveSelection, dispatch, onClear, onSearch, selectedFullAddress]
  );

  return (
    <>
      <LabelledField id="billingAddress">
        <LabelText>{melissaConfig.labelText}</LabelText>
        <div
          className="addressEntry"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded
          aria-owns="address-list"
          aria-controls="address-list"
        >
          <div className="spinner" aria-live="polite" role="status">
            <Spinner
              className={loading ? "spinnerVisible" : "spinnerInvisible"}
            />
            {loading && (
              <span className="screenReaderOnly">
                Loading address suggestions
              </span>
            )}
          </div>

          <input
            className="textField"
            type="text"
            inputMode="search"
            autoComplete="billing street-address"
            onKeyDown={handleKeyDown}
            onChange={onChange}
            placeholder={melissaConfig.placeholderText}
            value={value}
            aria-autocomplete="list"
            aria-describedby="address-hint"
          />
        </div>
        <div className="addressHint" id="address-hint">
          {numResults === null && <></>}
          {numResults === 0 && (
            <Text
              value={melissaConfig.noAddressesFoundTemplateText}
              args={{
                manualEntryButton: (
                  <button
                    type="button"
                    className="inlineButton"
                    onClick={handleShowFreeFormAddress}
                  >
                    {melissaConfig.manualEntryButtonText}
                  </button>
                ),
              }}
            />
          )}
          {numResults !== null && numResults > 0 && (
            <Text
              value={
                melissaConfig[
                  // eslint-disable-next-line no-nested-ternary
                  numResults > numShown
                    ? numResults >= maxNumShown
                      ? "tooManyAddressesFoundTemplateText"
                      : "manyAddressesFoundTemplateText"
                    : "someAddressesFoundTemplateText"
                ]
              }
              args={{
                numResults: (
                  <FormattedNumber locale={locale} value={numResults} />
                ),
                manualEntryButton: (
                  <button
                    type="button"
                    className="inlineButton"
                    onClick={handleShowFreeFormAddress}
                  >
                    {melissaConfig.manualEntryButtonText}
                  </button>
                ),
              }}
            />
          )}
        </div>
        <ul role="listbox" id="address-list">
          {pills}
        </ul>
      </LabelledField>
    </>
  );
};
